import { explorer } from "@/config";
import { useReverseNFD } from "@/hooks/use-nfd";
import { shorten } from "@/lib/utils";
import algosdk from "algosdk";
import { cx } from "class-variance-authority";
import { Link as RouterLink } from "react-router-dom";

export function Link({
  to,
  children,
  onClick,
  className,
  target = "_blank",
}: {
  to: string;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
  target?: string;
}) {
  const isExternal = to?.startsWith("http");
  if (isExternal) {
    return (
      <a
        href={to}
        className={cx("link", className)}
        onClick={onClick}
        target={target}
      >
        {children}
      </a>
    );
  }
  return (
    <RouterLink to={to} className={cx("link", className)} onClick={onClick}>
      {children}
    </RouterLink>
  );
}

export function AccountNoLink({
  address,
  resolveNFD = true,
  short,
}: {
  short?: true;
  resolveNFD?: boolean;
  address: string;
}) {
  if (!address) return null;
  const { data: nfd } = resolveNFD ? useReverseNFD(address) : { data: null }
  return (
      nfd ?? shorten(address, undefined, short ? false : undefined)
  );
}

export function AccountLink({
  address,
  resolveNFD = true,
  short,
  className,
}: {
  short?: true;
  resolveNFD?: boolean;
  address: string;
  className?: string;
}) {
  if (!address) return null;
  const { data: nfd } = resolveNFD ? useReverseNFD(address) : { data: null }
  return (
    <a href={`${explorer}/account/${address}`} target="_blank" className={cx("link", className)}>
      {nfd ?? shorten(address, undefined, short ? false : undefined)}
    </a>
  );
}

export function AssetLink({ id }: { id: number | bigint }) {
  if (!id) return null;
  return (
    <a href={`${explorer}/asset/${id}`} target="_blank" className="link">
      {Number(id)}
    </a>
  );
}

export function TransactionLink({ id }: { id: string }) {
  if (!id) return null;
  return (
    <a href={`${explorer}/transaction/${id}`} target="_blank" className="link">
      {shorten(id, 6, false)}
    </a>
  );
}

export function BlockLink({ id }: { id: number | bigint }) {
  if (!id) return null;
  return (
    <a href={`${explorer}/block/${id}`} target="_blank" className="link">
      {Number(id)}
    </a>
  );
}

export function ApplicationLink({ id }: { id: number | bigint }) {
  if (!id) return null;
  return (
    <a className="link" href={`${explorer}/application/${id}`} target="_blank">
      {Number(id)}
    </a>
  );
}

export function ApplicationEscrowLink({
  id,
  short,
}: {
  short?: true;
  id: number | bigint;
}) {
  if (!id) return null;
  const appAddr = algosdk.getApplicationAddress(Number(id));
  return <AccountLink short={short} address={appAddr} />;
}
